import { TriangleAlert } from 'lucide-react';
import { ReactElement } from 'react';
import { UseFormReturn } from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Switch,
  withTooltip,
} from '@crossnokaye/ux-common';
import {
  InputInlineValueChangeLabel,
  InputLabel,
  SwitchInput,
  SwitchInputContent,
} from '@crossnokaye/ux-common/ckds';

import { EditSettingsFormData } from '@/features/equipment-browser/equipment-details/details-form/equipment-details-form.types';
import { useFieldIsDirty } from '@/features/equipment-browser/equipment-details/settings/components/use-field-is-dirty';
import { EnhancedSetting } from '@/features/equipment-browser/subscriptions/use-get-settings-values';
import { getSourceUnits } from '@/features/equipment-browser/utils';
import { formatUnits } from '@/helpers/unit-formatter/unit-formatter';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';
import { Stack } from '@/ux-common/components/stack/stack';

// Matches the `dirty-event-cell.tsx` pending-indicator pattern in this same
// feature: wrap the icon directly with `withTooltip` rather than a generic
// span carrier.
const TriangleAlertWithTooltip = withTooltip(TriangleAlert);
const SwitchInputContentWithTooltip = withTooltip(SwitchInputContent);

interface SettingsFieldSwitchProps {
  setting: EnhancedSetting;
  form: UseFormReturn<EditSettingsFormData>;
  /**
   * Required form path. The dispatch layer (`general-settings.tsx`,
   * `sequencer-settings.tsx`, `subcomponent-attribute.tsx`) is the only
   * place that knows whether a setting belongs to the top-level `settings`
   * pool or the `sequencerSettings` pool, so it must pass the path
   * explicitly. Without this, the two pools previously collided in the
   * form's `settings` namespace and `useDetailsSettingsSave` lost track of
   * sequencer-toggled keys.
   */
  name: `settings.${string}` | `sequencerSettings.${string}`;
}

const TooltipContent = ({
  originalValue,
  currentValue,
}: {
  originalValue: boolean;
  currentValue: boolean;
}): ReactElement => {
  return (
    <div
      className={
        'm-0 flex items-center justify-center gap-0.5 whitespace-nowrap text-ckds-input-fg-new'
      }
    >
      {currentValue ? 'ON' : 'OFF'}
      <InputInlineValueChangeLabel
        className={'text-muted-foreground'}
        units={''}
        oldValue={originalValue ? 'ON' : 'OFF'}
      />
    </div>
  );
};

/**
 * Converts a form string value to a boolean for the Switch component.
 * Handles only formatted values like "True" for now.
 */
const stringToBoolean = (value: string | boolean | undefined): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    return lower === 'true';
  }
  return false;
};

/**
 * Switch field component for boolean settings.
 * Shows change indicator when value differs from original.
 */
export const SettingFieldSwitch = ({
  setting,
  form,
  name,
}: SettingsFieldSwitchProps): ReactElement => {
  const { isDisabled: disabledByPermissions, tooltipText } = useActionPermission({
    privilege: PRIVILEGES.writeCommands,
  });

  // Scoped dirty-state subscription. See `useFieldIsDirty` for why this is
  // form-level `dirtyFields` rather than `fieldState.isDirty`, and why we
  // scope by name rather than read the whole dirty map.
  const hasChanged = useFieldIsDirty(form.control, name);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const isChecked = stringToBoolean(field.value);

        // Convert boolean back to the formatted string value when switch changes
        const handleChange = (checked: boolean) => {
          const sourceUnits = getSourceUnits(setting);
          const formatted = formatUnits(
            { value: checked, units: sourceUnits },
            setting?.displayUnits
          );
          field.onChange(formatted.valueLabel);
        };

        return (
          <FormItem>
            <FormControl>
              <SwitchInput>
                <InputLabel
                  info={
                    <Stack className="max-w-96">
                      <p className="font-semibold">{setting.label}</p>
                      <p>{setting.description}</p>
                    </Stack>
                  }
                  infoTabStop={false}
                >
                  {setting.label}
                </InputLabel>
                <SwitchInputContentWithTooltip
                  size="default"
                  disabled={disabledByPermissions}
                  className="flex items-center justify-between gap-2"
                  tooltipContent={disabledByPermissions ? tooltipText : undefined}
                >
                  <Switch
                    checked={isChecked}
                    onCheckedChange={handleChange}
                    disabled={disabledByPermissions}
                  />
                  {hasChanged ? (
                    <TriangleAlertWithTooltip
                      aria-label={`Pending change for ${setting.label}`}
                      delayDuration={0}
                      className="h-4 w-4 text-ckds-base-caution"
                      aria-hidden={false}
                      tooltipContent={
                        <TooltipContent
                          originalValue={stringToBoolean(setting.value?.toString() ?? 'false')}
                          currentValue={isChecked}
                        />
                      }
                    />
                  ) : undefined}
                </SwitchInputContentWithTooltip>
              </SwitchInput>
            </FormControl>
            <FormMessage className={'pl-2'} />
          </FormItem>
        );
      }}
    />
  );
};
